import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { first, reduce, noop } from 'lodash';
import { useSelector } from 'react-redux';

import { googleAutocompleteSelector } from 'store/selectors/search';
import { Input } from 'components/core/Input';
import GoogleMapsAutocomplete from 'lib/googleMaps/autocomplete';
import './GoogleAutoComplete.scss';
import mapPin from 'assets/images/icons/map-pin.svg';
import { Image } from 'components/core/Image';
import { Paragraph } from 'components/core/Typography';
import Info from 'assets/images/icons/info.svg';
import useExperiment from 'hooks/useExperiment';

export function formatPlaceObject(place) {
	const params = {};

	if (place) {
		const addressComponents = reduce(
			place.address_components,
			(components, c) => ({
				...components,
				[first(c.types)]: c.short_name,
			}),
			{}
		);

		params.city = addressComponents.locality;
		params.zip = addressComponents.postal_code;
		params.state = addressComponents.administrative_area_level_1;
		params.locationSource = 'google_autocomplete';

		if (place.geometry) {
			params.latitude = place.geometry.location.lat();
			params.longitude = place.geometry.location.lng();
		}
	}
	return params;
}

export function GoogleAutocomplete({
	onChange = noop,
	value: valueProp = '',
	getRef,
	googleMapsKey,
	hideOnScroll = false,
	sf15342Variation,
	geolocation,
	...inputProps
}) {
	const [value, setValue] = useState(valueProp);
	const [loadedAutocomplete, setLoadedAutocomplete] = useState(false);
	const [showCustomDropdown, setShowCustomDropdown] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const textInputRef = useRef(null);
	const autoCompleteRef = useRef(null);
	const observerRef = useRef(null);
	const scrollHandlerRef = useRef(null);

	// Keep a ref to the latest onChange so the autocomplete callback always
	// calls the current version without needing to re-register the listener.
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	// Sync value from parent when it changes externally
	const prevValuePropRef = useRef(valueProp);
	useEffect(() => {
		if (prevValuePropRef.current !== valueProp && value !== valueProp) {
			setValue(valueProp);
		}
		prevValuePropRef.current = valueProp;
	}, [valueProp, value]);

	// Stable autocomplete callback using refs
	const onAutocomplete = useCallback((autocompleteValue, place) => {
		setValue(autocompleteValue);
		const locationParams = formatPlaceObject(place);
		onChangeRef.current({
			...locationParams,
			location: autocompleteValue,
		});
	}, []);

	// MutationObserver + scroll handler setup
	useEffect(() => {
		observerRef.current = new MutationObserver(() => {
			const el = document.querySelector('.pac-container');
			if (el) {
				if (sf15342Variation === 'variation1') {
					el.classList.add('pac-container-variation1');
					el.classList.remove('pac-container-default');
					el.style.marginTop = '23px';
				} else {
					el.classList.add('pac-container-default');
					el.classList.remove('pac-container-variation1');
					el.style.marginTop = '-5px';
				}
			}
		});

		observerRef.current.observe(document.body, {
			childList: true,
			subtree: true,
		});

		if (hideOnScroll) {
			scrollHandlerRef.current = (e) => {
				e.preventDefault();
				const el = document.querySelector('.pac-container');
				if (
					el &&
					el.style.display !== 'none' &&
					textInputRef.current === document.activeElement
				) {
					el.style.display = 'none';
				}
			};

			window.addEventListener('scroll', scrollHandlerRef.current, {
				passive: false,
			});
		}

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
			if (hideOnScroll && scrollHandlerRef.current) {
				window.removeEventListener('scroll', scrollHandlerRef.current);
			}
			GoogleMapsAutocomplete.removeAutocomplete(autoCompleteRef.current);
			autoCompleteRef.current = null;
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleFocus = useCallback(() => {
		if (!loadedAutocomplete) {
			GoogleMapsAutocomplete.configureAutocomplete(
				textInputRef.current,
				onAutocomplete,
				googleMapsKey
			).then((ac) => {
				autoCompleteRef.current = ac;
			});
			setLoadedAutocomplete(true);
		}

		setShowCustomDropdown(true);
		setErrorMessage('');
	}, [loadedAutocomplete, googleMapsKey, onAutocomplete]);

	const handleBlur = useCallback(() => {
		setTimeout(() => setShowCustomDropdown(false), 200);
	}, []);

	const handleChange = useCallback(
		(evt) => {
			const val = evt.target.value;
			setValue(val);
			onChange({ location: val });
		},
		[onChange]
	);

	const handleCustomOptionClick = useCallback(
		(geo) => () => {
			const location = geo.city + ', ' + geo.region;
			setValue('Detecting location...');
			setShowCustomDropdown(false);
			setErrorMessage('');
			if (geo) {
				onChange({
					location: location || 'Current Location',
				});
			} else {
				setValue('');
				setErrorMessage(
					'We weren\u2019t able to get your location. Please type it in manually.'
				);
			}
		},
		[onChange]
	);

	const handleRef = useCallback(
		(ref) => {
			textInputRef.current = ref;
			if (getRef) {
				getRef(ref);
			}
		},
		[getRef]
	);

	return sf15342Variation === 'variation1' ? (
		<div className="google-autocomplete">
			<Input
				placeholder=""
				value={value}
				{...inputProps}
				getRef={handleRef}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
			/>

			{showCustomDropdown && (
				<div
					className="custom-dropdown"
					onMouseDown={handleCustomOptionClick(geolocation)}
				>
					<Image
						src={mapPin}
						alt="Use my location"
						className="map-pin"
					/>
					Use My Location
				</div>
			)}
			{errorMessage && (
				<div className="error-message">
					<Image
						src={Info}
						alt="Info"
						className="info-icon"
					/>
					<Paragraph last>
						{
							'We weren\u2019t able to get your location. Please type it in manually.'
						}
					</Paragraph>
				</div>
			)}
		</div>
	) : (
		<div className="google-autocomplete">
			<Input
				placeholder=""
				value={value}
				{...inputProps}
				getRef={handleRef}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
			/>
		</div>
	);
}

GoogleAutocomplete.propTypes = {
	/** func onChange({location, city, zip, state, locationSource}) */
	onChange: PropTypes.func,
	value: PropTypes.string,
	getRef: PropTypes.func,
	googleMapsKey: PropTypes.string,
	hideOnScroll: PropTypes.bool,
	label: PropTypes.string,
};

/**
 * Wrapper component that provides Redux state and experiment
 * data via hooks to the GoogleAutocomplete.
 */
function GoogleAutocompleteConnected(props) {
	const googleMapsKey = useSelector(
		(state) => googleAutocompleteSelector(state).googleMapsKey,
	);
	const geolocation = useSelector((state) => state.user.geolocation);
	const { sf15342Variation } = useExperiment('sf_15342_geolocation_sfdc');

	return (
		<GoogleAutocomplete
			{...props}
			googleMapsKey={googleMapsKey}
			geolocation={geolocation}
			sf15342Variation={sf15342Variation}
		/>
	);
}

export default GoogleAutocompleteConnected;
